<template>
  <div class="card-modern">
    <div class="page-header mb-3">
      <div>
        <h4 class="card-title-modern">Report Overdue Penawaran Harga Biaya Angkut</h4>
        <p class="page-subtitle text-muted mb-0">
          Daftar penawaran harga (SPH) yang tanggal berakhirnya sudah lewat.
        </p>
      </div>
    </div>

    <!-- FILTER + EXPORT -->
    <div class="d-flex flex-wrap gap-2 align-items-center justify-content-end mb-3">
      <button
        class="btn btn-sm btn-outline-success"
        style="border-radius: 8px"
        :disabled="exporting"
        @click="exportExcel"
      >
        <span v-if="exporting">
          <span class="spinner-border spinner-border-sm me-1"></span> Exporting...
        </span>
        <span v-else>
          <i class="bi bi-file-earmark-excel me-1"></i>Export Excel
        </span>
      </button>

      <input
        v-model="filters.search"
        type="text"
        class="form-control form-control-sm"
        style="width: 180px"
        placeholder="Cari Perusahaan"
        @keyup.enter="fetchData(1)"
      />

      <select
        v-model="filters.status"
        class="form-select form-select-sm"
        style="width: 130px"
      >
        <option value="">Semua Status</option>
        <option value="1">Active</option>
        <option value="0">Inactive</option>
      </select>

      <button class="button-modern" @click="fetchData(1)">
        <i class="bi bi-search me-1"></i> Cari
      </button>
      <button class="btn btn-sm btn-outline-secondary" @click="resetFilter">
        Reset
      </button>
    </div>

    <!-- TABLE -->
    <div class="modern-table-wrapper">
      <table class="table-modern">
        <thead>
          <tr>
            <th style="width: 50px">No</th>
            <th>Vendor / Customer</th>
            <th>Tipe Kontrak</th>
            <th>Tanggal Mulai</th>
            <th>Tanggal Selesai</th>
            <th class="text-center">Overdue (Hari)</th>
            <th style="width: 80px" class="text-center">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="7" class="text-center py-4">
              <div class="spinner-border spinner-border-sm me-2"></div>
              Memuat data...
            </td>
          </tr>
          <tr v-else-if="data.length === 0">
            <td colspan="7" class="text-center py-4 text-muted">
              Tidak ada penawaran yang expired
            </td>
          </tr>
          <tr v-else v-for="(item, index) in data" :key="item.id">
            <td>{{ rowNumber(index) }}</td>
            <td>
              <span class="fw">{{ item.mst_corporate_name || "-" }}</span>
            </td>
            <td>{{ item.contract_type_name || "-" }}</td>
            <td>{{ formatDate(item.start_date) }}</td>
            <td>{{ formatDate(item.end_date) }}</td>
            <td class="text-center">
              <span class="badge-modern badge-danger">
                H+{{ item.overdue_days }}
              </span>
            </td>
            <td class="text-center">
              <button
                class="btn-icon-hover icon-primary"
                title="Detail"
                @click="openDetail(item)"
              >
                <i class="bi bi-eye"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- PAGINATION -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <small class="text-muted">
        Menampilkan
        {{ data.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1 }}
        –
        {{ Math.min(pagination.page * pagination.limit, pagination.total) }}
        dari {{ pagination.total }} data
      </small>
      <div class="d-flex align-items-center gap-2">
        <small class="text-muted">Rows per page</small>
        <select
          v-model.number="pagination.limit"
          class="form-select form-select-sm"
          style="width: 70px"
          @change="changePerPage"
        >
          <option v-for="opt in perPageOptions" :key="opt" :value="opt">
            {{ opt }}
          </option>
        </select>
        <nav>
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: pagination.page === 1 }">
              <button class="page-link" @click="fetchData(pagination.page - 1)">
                &#8249;
              </button>
            </li>
            <li
              v-for="(p, idx) in visiblePages"
              :key="idx"
              class="page-item"
              :class="{
                active: p === pagination.page,
                disabled: p === '...',
              }"
            >
              <button class="page-link" @click="p !== '...' && fetchData(p)">
                {{ p }}
              </button>
            </li>
            <li
              class="page-item"
              :class="{ disabled: pagination.page === totalPages }"
            >
              <button class="page-link" @click="fetchData(pagination.page + 1)">
                &#8250;
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import reportOverdueSPH from "@/services/reportOverdueSPHService";

const router = useRouter();
const route = useRoute();

const loading = ref(false);
const exporting = ref(false);
const data = ref([]);
const perPageOptions = [10, 25, 50, 100];

const filters = reactive({
  search: "",
  status: "",
});

const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0,
});

const totalPages = computed(
  () => Math.ceil(pagination.total / pagination.limit) || 1,
);

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = pagination.page;
  const delta = 2;
  const range = [];
  for (
    let i = Math.max(2, current - delta);
    i <= Math.min(total - 1, current + delta);
    i++
  ) {
    range.push(i);
  }
  if (current - delta > 2) range.unshift("...");
  if (current + delta < total - 1) range.push("...");
  range.unshift(1);
  if (total > 1) range.push(total);
  return range;
});

const buildParams = (extra = {}) => {
  const params = {
    page: pagination.page,
    limit: pagination.limit,
    sortBy: "overdue_days",
    sortDesc: "true",
    ...extra,
  };
  if (filters.search) params.search = filters.search;
  if (filters.status !== "") params.status = filters.status;
  return params;
};

const fetchData = async (page = 1) => {
  if (page < 1) return;
  loading.value = true;
  pagination.page = page;
  try {
    const res = await reportOverdueSPH.getList(
      buildParams({ page }),
    );
    data.value = res.data.data || [];
    pagination.page = res.data.pagination?.page || page;
    pagination.total = res.data.pagination?.total || 0;
  } catch (err) {
    console.error(err);
    alert("Gagal mengambil data SPH Overdue");
    data.value = [];
  } finally {
    loading.value = false;
  }
};

const changePerPage = () => {
  pagination.page = 1;
  fetchData(1);
};

const resetFilter = () => {
  filters.search = "";
  filters.status = "";
  fetchData(1);
};

const exportExcel = async () => {
  exporting.value = true;
  try {
    const res = await reportOverdueSPH.exportExcel(
      buildParams({ page: 1, limit: 0 }),
    );
    const blob = new Blob([res.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "SPH_Overdue.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error(err);
    alert("Gagal export Excel");
  } finally {
    exporting.value = false;
  }
};

const rowNumber = (idx) => (pagination.page - 1) * pagination.limit + idx + 1;

const formatDate = (date) => {
  if (!date) return "-";
  const s = String(date);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  return s;
};

const openDetail = (item) => {
  if (!item?.id) return;
  router.push({
    name: "transportoffersdetail",
    params: { id: item.id },
    query: { from: route.fullPath },
  });
};

onMounted(() => fetchData(1));
</script>